// @ts-nocheck
import { Component, Suspense } from 'react'
import { colors } from '@lmc/common/constants'
import Field from '../../../fields'
import { HelpLink } from '../../../generic'

/**
@description Renders the second step in the Add eMar Medication modal

@param {object} fields. The form fields.

@param {string} formId. addEmarMedication. Identifies the form.

@param {object} formState. The form state. Stores the medication name.

@param {function} goToNextStep. Fires when the Next Step button is pressed.
Takes the user to the next modal step

@param {object} resident. The resident.

@param {function} setFormField. Sets the form fields.

@param {function} validateFormFields. Validates the form fields and makes
sure that all required fields have been completed.

@returns {function} AddMedScheduleStep.
*/
interface AddMedScheduleStepProps {
  fields: Record<string, any>
  formId: string
  formState: Record<string, any>
  goToNextStep: () => void
  resident: Record<string, any>
  setFormField: (fieldName: string, value: any) => void
  validateFormFields: () => void
  mixpanel?: any // Add appropriate type
}

export default class AddMedScheduleStep extends Component<AddMedScheduleStepProps> {
  componentDidMount() {
    const { mixpanel } = this.props

    if (mixpanel) mixpanel.track('CO_emar_modal_selected_med')
  }

  handleChange = (value: any) => {
    const { goToNextStep } = this.props

    goToNextStep()
  }

  // Render field sections

  renderPRNField = () => {
    const { fields, formId, testId } = this.props
    const title =
      'Has this medication been prescribed as a scheduled medication or a not scheduled (PRN) medication?'
    const labelNote = (
      <div
        style={styles.labelNote}
        data-testid={testId ? `${testId}-prn-label-note` : 'prn-label-note'}
      >
        Scheduled medications will need to be signed at specific times. Not
        scheduled (when required/PRN) medications can be signed at any time.
      </div>
    )

    return (
      <div
        style={styles.fieldSubContainer}
        data-testid={
          testId ? `${testId}-prn-field-container` : 'prn-field-container'
        }
      >
        <Suspense fallback={<div>Loading...</div>}>
          <Field
            formId={formId}
            keys="PRN"
            label={title}
            labelChildren={labelNote}
            labelStyle={styles.label}
            layout="column"
            required
            compact
            type="radios"
            options={[
              { value: false, label: 'Scheduled' },
              { value: true, label: 'Not Scheduled (PRN)' }
            ]}
            onChange={this.handleChange}
            values={fields}
            testId={testId}
            data-testid={testId ? `${testId}-prn-field` : 'prn-field'}
          />
        </Suspense>
      </div>
    )
  }

  // Render section functions

  renderFields = () => {
    return <div style={styles.fieldsContainer}>{this.renderPRNField()}</div>
  }

  // Main render function

  render() {
    const { testId } = this.props
    return (
      <div
        style={styles.container}
        data-testid={
          testId ? `${testId}-add-med-schedule-step` : 'add-med-schedule-step'
        }
      >
        {this.renderFields()}
        <div style={styles.helpLinkContainer}>
          <HelpLink supportLink="https://support.logmycare.co.uk/en/articles/4851795-how-do-i-add-a-medication" />
        </div>
      </div>
    )
  }
}

const styles = {
  container: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    margin: 'auto',
    maxWidth: 960,
    padding: '0px 10px'
  },
  fieldsContainer: {
    width: '100%'
  },
  fieldSubContainer: {
    background: colors.white,
    borderRadius: 15,
    padding: 16,
    marginBottom: 16
  },
  label: {
    fontWeight: 700,
    fontSize: 16,
    lineHeight: '22px',
    marginBottom: 16
  },
  labelNote: {
    color: colors.darkgrey,
    fontSize: 12,
    fontWeight: 400
  }
}
